import { useState } from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import type { MRIMachinePopulated } from '../room-overview/types';

interface ScannerTemplatePickerProps {
    templates: MRIMachinePopulated[];
    selectedId: string | null;
    onSelect: (template: MRIMachinePopulated) => void;
    onClear: () => void;
}

function formatTemplateLabel(t: MRIMachinePopulated): string {
    const parts = [t.Model];
    if (t.Manufacturer) parts.push(`(${t.Manufacturer})`);
    parts.push(`— ${t.FieldStrength} T`);
    return parts.join(' ');
}

export default function ScannerTemplatePicker({
    templates,
    selectedId,
    onSelect,
    onClear,
}: ScannerTemplatePickerProps) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selected = templates.find((tmpl) => tmpl._id === selectedId);

    return (
        <div className="space-y-1.5">
            <Label>{t('onboarding.scannerTemplatePicker.label')}</Label>
            <div className="flex gap-2">
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            aria-label={t('onboarding.scannerTemplatePicker.ariaLabel')}
                            className="w-full justify-between font-normal shadow-sm hover:bg-transparent focus:ring-1 focus:ring-ring"
                        >
                            {selected
                                ? formatTemplateLabel(selected)
                                : t('onboarding.scannerTemplatePicker.placeholder')}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command>
                        <CommandInput placeholder={t('onboarding.scannerTemplatePicker.searchPlaceholder')} />
                        <CommandList>
                            <CommandEmpty>
                                {t('onboarding.scannerTemplatePicker.noResults')}
                            </CommandEmpty>
                            <CommandGroup>
                                {templates.map((template) => (
                                    <CommandItem
                                        key={template._id}
                                        value={formatTemplateLabel(template)}
                                        onSelect={() => {
                                            setOpen(false);
                                            setTimeout(() => {
                                                onSelect(template);
                                            }, 200);
                                        }}
                                        className="hover:bg-accent hover:text-accent-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                                    >
                                        <Check
                                            className={cn(
                                                'mr-2 h-4 w-4 shrink-0',
                                                selectedId === template._id
                                                    ? 'opacity-100'
                                                    : 'opacity-0'
                                            )}
                                        />
                                        <div>
                                            <p className="text-sm">
                                                {template.Model}
                                                {template.Manufacturer && (
                                                    <span className="ml-1 text-muted-foreground">
                                                        ({template.Manufacturer})
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {template.FieldStrength} T
                                            </p>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
                {selected && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={onClear}
                        aria-label={t('onboarding.scannerTemplatePicker.clearSelection')}
                    >
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}
